import {css, html} from 'lit';
import {customElement, property} from 'lit/decorators';
import {containerStyles, normalizeStyles, typographyStyles} from '@webs/components-library/theming';
import {GridColumn} from '@webs/components-library/grid-column';

@customElement('webs-card-resource')

export class CardResource extends GridColumn {

  private propertyMap: Map<string, unknown> = new Map();

  static styles = [
    ...GridColumn.styles,
    normalizeStyles,
    containerStyles,
    typographyStyles,
    css`
    :host {
        --card-justify-content: space-between;
        --content-height: 100%;
     }
      a {
        font-family: var(--font-family-base);
        font-weight: var(--font-weight-base);
      }

      .m-cards-resources__card {
        margin-bottom: 1.5rem;
      }

      webs-logo::part(logo) {
        position: absolute;
        z-index: 20;
        right: 16px;
        top: 16px;
        padding: 0;
        width: 120px;
        height: 25px;
      }

      webs-tag::part(tag) {
        position: absolute;
        z-index: 20;
        right: 16px;
        top: 16px;
      }

      webs-link-arrow,
      webs-button {
        display: block;
        margin-top: 1.2rem;
      }

      .m-cards-resources__card-resource {
        display: flex;
        flex-direction: column;
        height: 100%;
        justify-content: var(--card-justify-content);
        position: relative;
        text-decoration: none;
      }

      .m-cards-resources__card-resource .card-content
        {
          display: flex;
          flex-direction: column;
          height: var(--content-height);
          justify-content: space-between;
          margin-top: 1rem;
        }
      .m-cards-resources__card-resource .tag {
        color: var(--color-orange);
        font-family: var(--font-family-alternative);
        font-size: 0.7rem;
        margin-bottom: .8rem;
        text-transform: uppercase;
      }
      .m-cards-resources__card-resource .intro {
        color: var(--color-base);
        font-family: var(--font-family-alternative);
        font-size: .9rem;
        font-weight: var(--font-weight-base);
      }

      .m-cards-resources__card-resource .date,
      .m-cards-resources__card-resource .time {
        color: var(--color-grey);
        font-family: var(--font-family-alternative);
        font-size: .9rem;
        line-height: 1.5;
      }

      .m-cards-resources__card-resource .date {
        margin-right: 4px;
        padding-right: 6px;
        position: relative;
      }

      .m-cards-resources__card-resource .date::after {
        background: var(--color-grey);
        content: '';
        display: block;
        height: calc(100% - 2px);
        right: 0;
        top: 0;
        position: absolute;
        width: 1px;
      }

      @media only screen and (min-width: 992px){
        .m-cards-resources__card-resource .intro {
          font-size: 1.05rem;
        }
      }
    `
  ];

  @property()
  tag: string;

  @property()
  set title(value: string) {
    this.onPropertyChange('title', value);
  }

  get title(): string {
    return this.propertyMap.get('title') as string;
  }

  @property({attribute: 'date'})
  publishDate: string;

  @property({attribute: 'time'})
  readingTime: string;

  @property()
  href: string;

  @property()
  openInNewTab: boolean;

  @property()
  rel: string;

  @property()
  set image(value: string) {
    this.onPropertyChange('image', value);
  }

  get image(): string {
    return this.propertyMap.get('image') as string;
  }

  @property()
  height: string

  @property({type: Number})
  width: number;

  @property()
  type = 'cover';

  @property({attribute: 'button-type'})
  buttonType: string;

  @property({attribute: 'button-href'})
  buttonHref: string;

  @property({attribute: 'button-title'})
  buttonTitle: string;

  @property({attribute: 'open-button-in-new-tab'})
  openButtonInNewTab: string;

  @property({attribute: 'logo-src'})
  logoSrc: string;

  @property({attribute: 'event-tag'})
  eventTag: string;

  @property({attribute: 'event-tag-gradient'})
  eventTagGradient: string;

  protected render(): unknown {
    return html`
      ${this.href ? html`
        <a class="m-cards-resources__card-resource" target="${this.openInNewTab ? '_blank' : ''}"
           rel="${this.rel ? this.rel : ''}"
           href="${this.href}">
          ${this.logoSrc ? html`
            <webs-logo src="${this.logoSrc}" alt="Logo" loading="lazy" width="120" size="small"></webs-logo>
          ` : ''}
          ${this.eventTag ? html`
            <webs-tag text="${this.eventTag}" gradient="${this.eventTagGradient}"></webs-tag>
          ` : ''}
          <webs-media src="${this.image}" alt="${this.title}" width="${this.width}" height="${this.height}"
                      type="${this.type}"></webs-media>
          <div class="card-content">
            <div class="info">
              <h6 class="tag">${this.tag}</h6>
              <p class="intro">${this.title}</p>
            </div>
            ${this.publishDate || this.readingTime ? html`
              <p>
                ${this.publishDate ? html`
                  <span class="date">${this.publishDate}</span>` : ''}

                ${this.readingTime ? html`
                  <span class="time">${this.readingTime}</span>` : ''}
              </p>` : ''}
          </div>
        </a>
      ` : html`
        <div class="m-cards-resources__card-resource">
          ${this.logoSrc ? html`
            <webs-logo src="${this.logoSrc}" alt="Logo" loading="lazy" width="120" size="small"></webs-logo>
          ` : ''}
          ${this.eventTag ? html`
            <webs-tag text="${this.eventTag}" gradient="${this.eventTagGradient}"></webs-tag>
          ` : ''}
          <webs-media src="${this.image}" alt="${this.title}" width="${this.width}" height="${this.height}"
                      type="${this.type}"></webs-media>
          <div class="card-content">
            <div class="info">
              <h6 class="tag">${this.tag}</h6>
              <p class="intro">${this.title}</p>
            </div>
            ${this.publishDate || this.readingTime ? html`
              <p>
                ${this.publishDate ? html`
                  <span class="date">${this.publishDate}</span>` : ''}

                ${this.readingTime ? html`
                  <span class="time">${this.readingTime}</span>` : ''}
              </p>` : ''}
          </div>

          ${this.buttonTitle ? html`
            ${this.buttonType == 'link' ? html`
                <webs-link-arrow href="${this.buttonHref}" text="${this.buttonTitle}"
                                 new-tab="${this.openButtonInNewTab}"></webs-link-arrow>` :
              html`
                <webs-button href="${this.buttonHref}" text="${this.buttonTitle}"
                             new-tab="${this.openButtonInNewTab}"></webs-button>
              `}` : ''}
        </div>
      `}
    `
  }

  private onPropertyChange(name: string, value: unknown): void {
    const oldValue = this.propertyMap.get(name);
    this.propertyMap.set(name, value);
    this.requestUpdate(name, oldValue);
  }
}
